/**
 * QuickAddModal — universal 3-click capture for any entity type.
 *
 * The "ticketing system" answer to: dump first, classify in flow.
 *
 * 3-click path (worst case):
 *   1. Open modal (Cmd+N or topbar `+` button)
 *   2. Click a type chip
 *   3. Click Save
 *
 * Fast paths:
 *   - Type → AI heuristic pre-selects the chip → user hits Cmd+Enter (0 clicks)
 *   - Just type and hit Enter → defaults to Task (0 clicks)
 *
 * Why a modal (not a route)?
 *   - Preserves the user's current view (Today, Inbox, wherever).
 *   - Cmd+N is global — works from any page.
 *   - "Save & add another" lets you chain 10 captures without leaving.
 *
 * Layout is split into four subcomponents (Header / Body /
 * TypeChips / Footer) so this file reads as orchestration + state.
 *
 * Each type fans out to its existing POST endpoint via the `api`
 * client — no new backend routes needed. The form-shape table
 * (`FIELDS_BY_TYPE`) drives both the rendering and the payload
 * builder, so adding a new type is a single map entry.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import api from '../api';
import { suggestType } from '../utils/suggestType';
import { logger } from '../utils/logger';
import { TYPE_BY_KEY, secondaryFieldFor, buildPayload } from './quickadd/typeConfig';
import { QuickAddHeader } from './quickadd/QuickAddHeader';
import { QuickAddBody } from './quickadd/QuickAddBody';
import { QuickAddTypeChips } from './quickadd/QuickAddTypeChips';
import { QuickAddFooter } from './quickadd/QuickAddFooter';

const SUGGESTION_DEBOUNCE_MS = 300;

export default function QuickAddModal({ open, onClose, initialBrainDump = false }) {
  const [state, setState] = useState({ title: '' });
  const [type, setType] = useState(null);       // user choice (overrides suggestion)
  const [suggestion, setSuggestion] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedToast, setSavedToast] = useState(null);
  // Brain dump mode — Save defaults to "keep open + clear", session
  // counter visible in header, slight visual differentiation. Designed
  // for rapid-fire capture (10 ideas in a minute without losing flow).
  const [brainDump, setBrainDump] = useState(initialBrainDump);
  // Routing toggle — when ON, items go to /api/inbox regardless of
  // the picked type chip (the type is stored as metadata so the
  // Inbox triage UI can offer a one-click conversion). Default ON in
  // Brain Dump mode so rapid-fire captures land safely in the
  // triage queue; defaults OFF in normal Quick Add so the existing
  // "capture-to-type" muscle memory keeps working.
  const [routeToInbox, setRouteToInbox] = useState(initialBrainDump);
  const [capturedThisSession, setCapturedThisSession] = useState(0);
  const [inboxCount, setInboxCount] = useState(null);
  const inputRef = useRef(null);
  const suggestionTimer = useRef(null);

  // On open: reset transient state, sync brain-dump mode from prop,
  // and (in brain-dump mode) fetch current inbox count so the header
  // counter has a denominator to show.
  //
  // useLayoutEffect (not useEffect) — testing agent flagged a 1-frame
  // race where useEffect's state writes landed AFTER the first paint,
  // causing the header to briefly render in stale brainDump state on
  // reopen. Layout effects run synchronously before the browser
  // commits the frame, eliminating the flash.
  useLayoutEffect(() => {
    if (open) {
      setState({ title: '' });
      setType(null);
      setSuggestion(null);
      setSavedToast(null);
      setBrainDump(initialBrainDump);
      // Default routing mirrors brain-dump default — opt-in to
      // inbox for rapid-fire flow, direct-to-type for normal quick
      // add. User can toggle either way once the modal is open.
      setRouteToInbox(initialBrainDump);
      setCapturedThisSession(0);
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open, initialBrainDump]);

  // Refetch the inbox count whenever we ENTER brain-dump mode while
  // the modal is open (covers both prop-driven open and the in-modal
  // toggle). Separate from the open effect so a mid-session toggle
  // doesn't lose the session counter.
  useEffect(() => {
    if (!open || !brainDump) { setInboxCount(null); return undefined; }
    let cancelled = false;
    api.get('/counts').then((res) => {
      if (cancelled) return;
      const n = res?.data?.inbox;
      setInboxCount(typeof n === 'number' ? n : null);
    }).catch(() => { /* non-fatal — counter just hides */ });
    return () => { cancelled = true; };
  }, [open, brainDump]);

  // Debounced AI heuristic suggestion. Purely local — no network call.
  useEffect(() => {
    clearTimeout(suggestionTimer.current);
    const text = state.title;
    if (!text || text.length < 4) { setSuggestion(null); return undefined; }
    suggestionTimer.current = setTimeout(() => {
      const result = suggestType(text);
      if (result.type) {
        setSuggestion(result);
        // Auto-merge hints into state IF the user hasn't typed into
        // the secondary field yet — never overwrite explicit input.
        setState((s) => {
          const merged = { ...s };
          if (result.hints.url && !s.url) merged.url = result.hints.url;
          if (result.hints.amount && !s.amount) merged.amount = result.hints.amount;
          if (result.hints.contactName && !s.contactName) merged.contactName = result.hints.contactName;
          if (result.hints.stakeholder && !s.stakeholder) merged.stakeholder = result.hints.stakeholder;
          return merged;
        });
      }
    }, SUGGESTION_DEBOUNCE_MS);
    return () => clearTimeout(suggestionTimer.current);
  }, [state.title]);

  const effectiveType = type || suggestion?.type || 'task';
  const secondary = useMemo(() => secondaryFieldFor(effectiveType, state, setState), [effectiveType, state]);

  const acceptSuggestion = useCallback(() => {
    if (suggestion?.type) setType(suggestion.type);
  }, [suggestion]);

  const save = useCallback(async ({ andAddAnother } = {}) => {
    if (!state.title.trim() || saving) return;
    // In brain-dump mode, save defaults to "keep open + clear". The
    // user only sees one button ("Capture") and every save behaves as
    // a chain step. Explicit Cmd+Enter / Shift+Cmd+Enter still works
    // (Shift→add-another stays additive to brainDump-default).
    const shouldChain = brainDump ? (andAddAnother !== false) : (andAddAnother === true);
    setSaving(true);
    try {
      // Two routing paths — `routeToInbox` flag wins:
      //   ON  → POST /api/inbox with the title only (the heuristic
      //         on the server attaches a suggested type that the
      //         Inbox triage UI surfaces as a one-click conversion).
      //   OFF → existing `buildPayload` path — direct create in the
      //         typed collection, same as before today.
      const [path, payload] = routeToInbox
        ? ['/inbox', { title: state.title.trim() }]
        : buildPayload(effectiveType, state);
      const res = await api.post(path, payload);
      const created = res?.data;
      // Toast label reflects what actually happened — "Inbox" for the
      // triage route, the type label for direct-to-type captures.
      // Users need this feedback to build muscle memory around the
      // toggle; without it, both modes feel identical.
      setSavedToast(routeToInbox
        ? { type: 'inbox', label: 'Inbox', id: created?.id }
        : { type: effectiveType, label: TYPE_BY_KEY[effectiveType]?.label, id: created?.id }
      );
      // Broadcast so lists/badges refresh
      try { window.dispatchEvent(new Event('polkdesq:data-mutated')); } catch { /* ignore */ }
      if (brainDump) {
        setCapturedThisSession((n) => n + 1);
        // Refresh inbox count opportunistically in case the user
        // captured an inbox-bound entity (note/task with no business).
        api.get('/counts').then((r) => {
          const n = r?.data?.inbox;
          if (typeof n === 'number') setInboxCount(n);
        }).catch(() => { /* ignore */ });
      }
      if (shouldChain) {
        setState({ title: '' });
        setType(null);
        setSuggestion(null);
        setTimeout(() => inputRef.current?.focus(), 60);
      } else {
        setTimeout(() => onClose?.(), 350);
      }
    } catch (err) {
      logger.error('QuickAdd save failed', { type: effectiveType, routeToInbox, err });
      setSaving(false);
      return;
    }
    setSaving(false);
  }, [state, effectiveType, saving, onClose, brainDump, routeToInbox]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose?.(); return; }
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      save({ andAddAnother: e.shiftKey });
      return;
    }
    // Plain Enter in the title input saves (Shift+Enter for newline).
    if (e.key === 'Enter' && !e.shiftKey && e.target?.tagName === 'TEXTAREA' && e.target?.dataset?.testid === 'quickadd-title') {
      e.preventDefault();
      save();
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="quickadd-modal"
      className="fixed inset-0 z-[300] flex items-start justify-center pt-[12vh] px-4"
      style={{ background: 'rgba(5,5,7,0.62)', backdropFilter: 'blur(28px) saturate(140%)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      onKeyDown={onKeyDown}
    >
      <div
        className="w-full max-w-[600px] rounded-[24px] overflow-hidden"
        style={{
          background: 'var(--pd-surface-3)',
          // Brain-dump mode gets a warm amber halo so the user
          // visually KNOWS they're in the rapid-fire mode (and that
          // hitting Enter won't dismiss the modal). The amber pairs
          // with the toggle pill + Brain icon + captured-count
          // colour — one consistent "brain dump = warm" cue.
          border: '1px solid ' + (brainDump ? 'var(--pd-brand)' : 'var(--pd-border-highlight)'),
          boxShadow: brainDump
            ? '0 0 0 4px var(--pd-brand-soft), var(--pd-shadow-command), var(--pd-edge-highlight)'
            : 'var(--pd-shadow-command), var(--pd-edge-highlight)',
          animation: 'pd-modal-rise 320ms cubic-bezier(0.34, 1.56, 0.64, 1) both',
        }}
      >
        <QuickAddHeader
          brainDump={brainDump}
          capturedThisSession={capturedThisSession}
          inboxCount={inboxCount}
          routeToInbox={routeToInbox}
          onToggleRouteToInbox={() => setRouteToInbox((v) => !v)}
          onToggleBrainDump={() => setBrainDump((b) => !b)}
          onClose={onClose}
        />

        <QuickAddBody
          inputRef={inputRef}
          title={state.title}
          onTitleChange={(value) => setState((s) => ({ ...s, title: value }))}
          suggestion={suggestion}
          showSuggestion={!!(suggestion?.type && !type)}
          onAcceptSuggestion={acceptSuggestion}
          secondary={secondary}
        />

        <QuickAddTypeChips
          effectiveType={effectiveType}
          explicitType={type}
          onSelectType={setType}
        />

        <QuickAddFooter
          brainDump={brainDump}
          saving={saving}
          canSave={!!state.title.trim()}
          savedToast={savedToast}
          onSave={() => save()}
          onSaveAndAddAnother={() => save({ andAddAnother: true })}
        />
      </div>
    </div>
  );
}
